import * as React from "react";
import { useNavigate } from "react-router-dom";
import { Tooltip } from "@/components/ui/misc";
import { SearchableFieldsDialog } from "@/features/searchable-fields-dialog";
import { useAuth } from "@/lib/auth";
import { searchHint } from "@/lib/forms";
import { useSearchedFields, useTenants } from "@/lib/queries";
import { atLeast, type FormEntity } from "@/lib/types";

/**
 * A search box with GoHighLevel's hint over it (ADR-101): hovering the box says what the search looks in,
 * the platform's own fields first and then the fields an admin marked searchable that this person's search
 * reads, and an admin gets *Customize here*, which opens Edit searchable fields where they stand. Saving
 * there refreshes the hint and any search already on screen.
 *
 * The hint is the server's own account of the search (`GET /forms/{entity}/searchable`), read by the rule
 * the search itself uses, so it names what the search does: a rep is not told of a field hidden from reps,
 * and a superadmin over several tenants is told every tenant's. Which fields are searched is each tenant's
 * choice, so a superadmin with several tenants is taken to the Custom fields page, where the tenant is
 * picked first; with one tenant they customize in place, as an admin does.
 *
 * The box keeps its own label and placeholder: the hint is an aid, and a screen reader hears it as the
 * box's description (the sentence alone; the Custom fields page is the keyboard's way to the same dialog).
 */
export function SearchHint({ entity, children }: { entity: FormEntity; children: React.ReactElement }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const admin = atLeast(user?.role, "admin");
  const superadmin = user?.role === "superadmin";
  const searched = useSearchedFields(entity);
  const tenants = useTenants({ enabled: superadmin });
  const soleTenant = tenants.data?.length === 1 ? (tenants.data[0]?.id ?? null) : null;
  const inPlace = admin && (!superadmin || soleTenant !== null);
  const [hintOpen, setHintOpen] = React.useState(false);
  const [editing, setEditing] = React.useState(false);

  const hint = searchHint(entity, (searched.data?.fields ?? []).map((f) => f.title));
  const customize = () => {
    setHintOpen(false);
    if (inPlace) setEditing(true);
    else navigate(`/custom-fields?object=${entity}`);
  };

  return (
    <>
      <Tooltip
        open={hintOpen}
        onOpenChange={setHintOpen}
        label={hint}
        content={
          <span>
            {hint}
            {admin && (
              <>
                {" "}
                <button
                  type="button"
                  className="font-medium underline underline-offset-2 hover:no-underline"
                  // Pressing it must not take the focus from the search box: the box losing focus closes
                  // the hint, and the click would land on nothing (somebody who typed a search first).
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={customize}
                >
                  Customize here
                </button>
              </>
            )}
          </span>
        }
      >
        {children}
      </Tooltip>
      {editing && (
        <SearchableFieldsDialog
          open
          onOpenChange={(open) => !open && setEditing(false)}
          entity={entity}
          tenantId={superadmin ? soleTenant : null}
        />
      )}
    </>
  );
}
